/** Styles for Blueprint */
import { ApolloProvider } from "@apollo/client";
import "@blueprintjs/core/lib/css/blueprint.css";
import "@blueprintjs/datetime/lib/css/blueprint-datetime.css";
import Bugsnag from "@bugsnag/js";
import BugsnagPluginReact from "@bugsnag/plugin-react";
import { log, useAnalyticsSpan } from "@hackthenorth/analytics";
import { AuthProvider, JWTRole, North } from "@hackthenorth/north";
import { Worker as PDFWorker } from "@phuocng/react-pdf-viewer";
import "@phuocng/react-pdf-viewer/cjs/react-pdf-viewer.css";
import "north.js/dist/style.css";
import React, { memo, useEffect, useState } from "react";
import Modal from "react-modal";
import { QueryClient, QueryClientProvider } from "react-query";
import {
  BrowserRouter,
  Route as RouterRoute,
  Navigate,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import styled from "styled-components";
import UAParser from "ua-parser-js";
import { QueryParamProvider } from "use-query-params";

import { client } from "src/api/client";
import {
  GlobalStyles,
  JudgingBanner,
  JUDGING_BANNER_HEIGHT,
  QA,
  Route,
  Sidebar,
  Toast,
} from "src/shared/components";
import { LEGACY_MENTORSHIP_ENABLED } from "src/shared/constants/featureFlags";
import { BaseRoute, Route as RouteName } from "src/shared/constants/route";
import {
  DataProvider,
  EventContextProvider,
  MentorContextProvider,
  NetworkingContextProvider,
  Permission,
  PermissionsContextProvider,
  SidebarContextProvider,
  //   useHackerContext,
  useEventContext,
  UserContextProvider,
  useSidebarContext,
  useUserContext,
} from "src/shared/contexts";
import { LocalAuthProvider, LocalLogin } from "src/shared/contexts/LocalAuth";
// import { HackerStage } from "src/shared/contexts/HackerContext/types";
import { useDeviceSize } from "src/shared/hooks";
// import { hideSidebarStyles } from "src/shared/contexts/SidebarContext/styles";
import { hasRsvpAccess } from "src/shared/utils/attendeeAccess";
import { APP_ENV, IS_PRODUCTION, IS_LOCAL_API } from "src/shared/utils/env";
import themeDef from "src/theme";
import NotFound from "src/views/404";
import { BadgeRoutes } from "src/views/badge/routes";
import { DocumentRoutes } from "src/views/documents/routes";
import AwSnap from "src/views/error/AwSnap";
import UnauthorizedError from "src/views/error/UnauthorizedError";
import { HackerRoutes } from "src/views/hacker/routes";
import HackerRsvpHeader, {
  RSVPHEADER_HEIGHT,
  RSVPHEADER_TABLET_HEIGHT,
} from "src/views/hacker/rsvp/header";
import HackerRsvpSidebar from "src/views/hacker/rsvp/rsvp-sidebar";
import { HelpRoutes } from "src/views/help/routes";
import AttendeeHome from "src/views/home/AttendeeHome";
import { MentorRoutes } from "src/views/mentor/routes";
import { PlaygroundRoutes } from "src/views/playground/routes";
import Register from "src/views/register";
import { ScheduleRoutes } from "src/views/schedule/routes";
import { SponsorRoutes } from "src/views/sponsor/routes";
import { VolunteerRoutes } from "src/views/volunteer/routes";

import AccountManagement from "./views/account";
import { GooseGamesRoutes } from "./views/goose-games/routes";
import { HackerDirectoryRoutes } from "./views/hacker-directory/routes";
import { HardwareRoutes } from "./views/hardware/routes";
import { LinkAccountRoutes } from "./views/link-account/routes";
import Networking from "./views/networking";
import NetworkingResult from "./views/networkingResult";
import Settings from "./views/settings";
import { SignageHuntRoutes } from "./views/signage-hunt/routes";

Modal.setAppElement("#root");

const queryClient = new QueryClient();

Bugsnag.start({
  apiKey: "fcc22e94006ad0b4c21cf5bd345065c5",
  plugins: [new BugsnagPluginReact()],
});
const ErrorBoundary = Bugsnag.getPlugin("react")!.createErrorBoundary(React); // eslint-disable-line

const AppShell = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100vh;
`;

const Container = styled.div<{ $bannerHeight: number }>`
  position: relative;
  display: flex;
  width: 100%;
  height: 100%;
  min-height: calc(100vh - ${({ $bannerHeight }) => $bannerHeight}px);
`;

const MainContent = styled.div<{
  $bannerHeight: number;
  showNavSidebar: boolean;
  showRsvpHeader: string | boolean;
  showRsvpSidebar: string | boolean;
  sidebarWidth: number;
  isTabletOrSmaller: boolean;
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  top: ${({ showRsvpHeader, isTabletOrSmaller }) =>
    !showRsvpHeader
      ? 0
      : isTabletOrSmaller
      ? RSVPHEADER_TABLET_HEIGHT
      : RSVPHEADER_HEIGHT}px;
  left: ${({ sidebarWidth }) => sidebarWidth}px;
  min-height: calc(
    100vh - ${({ $bannerHeight }) => $bannerHeight}px -
      ${({ showRsvpHeader, isTabletOrSmaller }) =>
        !showRsvpHeader
          ? 0
          : isTabletOrSmaller
          ? RSVPHEADER_TABLET_HEIGHT
          : RSVPHEADER_HEIGHT}px
  );
  width: calc(100% - ${({ sidebarWidth }) => sidebarWidth}px);
`;

const INDICATOR_COLOR: string = {
  production: "green",
  staging: "yellow",
  latest: "red",
  development: "grey",
}[APP_ENV];

const Indicator = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 4px;
  z-index: 1000;
  background-color: ${INDICATOR_COLOR};
`;

const EnvIndicator = memo(function EnvIndicator() {
  return IS_PRODUCTION ? null : <Indicator />;
});

const RouteAdapter = ({ children }: any) => {
  const navigate = useNavigate();
  const location = useLocation();

  const adaptedHistory = React.useMemo(
    () => ({
      replace(location: any) {
        navigate(location, { replace: true, state: location.state });
      },
      push(location: any) {
        navigate(location, { replace: false, state: location.state });
      },
    }),
    [navigate]
  );
  return children({ history: adaptedHistory, location });
};

const ScrollToTopWrapper = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {IS_LOCAL_API && (
        <RouterRoute path="/local-login" element={<LocalLogin />} />
      )}
      <Route path={BaseRoute.HOME} element={<AttendeeHome />} />
      <Route
        path={BaseRoute.SCHEDULE}
        element={<ScheduleRoutes />}
        requirePermissions={[Permission.VIEW_FULL_SCHEDULE]}
        requireHackerPostRsvpTools
      />
      <Route
        path={`${BaseRoute.PLAYGROUND}/*`}
        element={<PlaygroundRoutes />}
      />
      <Route
        path={`${BaseRoute.CONNECT_SLACK_ACCOUNT}/*`}
        element={<LinkAccountRoutes />}
        requireAuth={true}
      />
      <Route path={`${BaseRoute.DOCUMENTS}/*`} element={<DocumentRoutes />} />
      <Route path={`${BaseRoute.HELP}/*`} element={<HelpRoutes />} />
      <Route
        path={`${BaseRoute.BADGE}/*`}
        element={<BadgeRoutes />}
        requireAuth
      />
      <Route
        path={`${BaseRoute.SPONSOR}/*`}
        element={<SponsorRoutes />}
        requirePermissions={[Permission.SPONSOR]}
      />
      <Route
        path={`${BaseRoute.HACKER_DIRECTORY}/*`}
        element={<HackerDirectoryRoutes />}
        requirePermissions={[
          Permission.HACKER_EVENT_ACCESS,
          Permission.ORGANIZER,
        ]}
        requireAll={false}
      />
      <Route
        path={`${BaseRoute.HACKER}/*`}
        element={<HackerRoutes />}
        requirePermissions={[Permission.RSVP]}
      />
      <Route
        path={`${BaseRoute.VOLUNTEER}/*`}
        element={<VolunteerRoutes />}
        requirePermissions={[Permission.VOLUNTEER_EVENT_ACCESS]}
        permissionDeniedTitle="Complete your RSVP"
        permissionDeniedMessage="Finish your volunteer RSVP before accessing the volunteer tool."
        permissionDeniedActionLabel="Go to RSVP"
        permissionDeniedRedirectTo={RouteName.HACKER_RSVP}
        permissionDeniedFor={Permission.VOLUNTEER_EVENT_ACCESS}
      />
      <Route
        path={`${BaseRoute.GOOSE_GAMES}/*`}
        element={<GooseGamesRoutes />}
        requirePermissions={[
          Permission.HACKER_EVENT_ACCESS,
          Permission.ORGANIZER,
        ]}
        requireAll={false}
        requireHackerPostRsvpTools
      />
      {LEGACY_MENTORSHIP_ENABLED && (
        <Route
          path={`${BaseRoute.MENTOR}/*`}
          element={
            <MentorContextProvider>
              <MentorRoutes />
            </MentorContextProvider>
          }
          requirePermissions={[Permission.MENTOR]}
        />
      )}
      <Route
        path={`${BaseRoute.HARDWARE}/*`}
        element={<HardwareRoutes />}
        requirePermissions={[
          Permission.HACKER_EVENT_ACCESS,
          Permission.VOLUNTEER_EVENT_ACCESS,
          Permission.ORGANIZER,
        ]}
        requireAll={false}
        requireHardwareOpen
      />
      <Route
        path={`${BaseRoute.NETWORKING_RESULT}/*`}
        element={<NetworkingResult />}
        requireAuth={true}
        requireHackerPostRsvpTools
      />
      <Route
        path={`${BaseRoute.SETTINGS}/*`}
        element={
          <NetworkingContextProvider>
            <Settings />
          </NetworkingContextProvider>
        }
        requireAuth={true}
        requireHackerPostRsvpTools
      />
      <Route
        path={`${BaseRoute.ACCOUNT}/*`}
        element={<AccountManagement />}
        requireAuth={true}
      />
      <Route
        path={`${BaseRoute.NETWORKING}/*`}
        element={
          <NetworkingContextProvider>
            <Networking />
          </NetworkingContextProvider>
        }
        requireAuth={true}
        requireHackerPostRsvpTools
      />
      <Route
        path={`${BaseRoute.SIGNAGE_HUNT}/*`}
        element={<SignageHuntRoutes />}
        requireAuth={true}
        requireHackerPostRsvpTools
      />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

const AppContent = () => {
  const [sidebarWidth, setSidebarWidth] = useState(0);
  useEffect(() => {
    const userAgent = new UAParser();

    const deviceType = userAgent.getDevice().type;

    /**
     * Possible device types: console, mobile, tablet, smarttv, wearable, embedded
     */
    log({
      identifier: deviceType ?? "desktop",
      duration: 0,
      startTime: 0,
      stream: "platform",
    });
    if (!("Notification" in window)) {
      console.debug("This browser does not support notifications.");
    } else {
      log({
        identifier: Notification?.permission,
        duration: 0,
        startTime: 0,
        stream: "notifications",
      });
    }
  }, []);

  // Function to calculate and update the sidebar width
  const updateSidebarWidth = () => {
    setSidebarWidth(document.getElementById("sidebar")?.clientWidth ?? 257);
  };

  useEffect(() => {
    // on window resize
    const handleResize = () => {
      updateSidebarWidth();
    };

    window.addEventListener("resize", handleResize);
    updateSidebarWidth(); // Set sidebar width on initial render
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Call updateSidebarWidth on every render to keep it updated
  useEffect(() => {
    updateSidebarWidth();
  });

  const { isAuthenticated, roles, isOrganizer } = useUserContext();
  const { judging } = useEventContext();
  const isTabletOrSmaller = useDeviceSize("tablet");

  const { showSidebar } = useSidebarContext();
  const location = useLocation();
  useAnalyticsSpan(location.pathname, "navigation");

  const checkHackerPathname = () => {
    switch (location.pathname) {
      case RouteName.HACKER_PERSONAL:
        return "Personal Information";
      case RouteName.HACKER_TRAVEL:
        return "Travel";
      case RouteName.HACKER_EVENT:
        return "Event Information";
      case RouteName.HACKER_BADGE:
        return "Your Badge";
      case RouteName.HACKER_CAREER:
        return "Career";
      case RouteName.HACKER_PROFILE:
        return "Directory Profile";
      case RouteName.HACKER_CONFIRMATION:
        return "Confirmation";
      case RouteName.HACKER_TRAVEL_DETAILS:
        return "Travel Details";
      default:
        return false;
    }
  };

  const shouldShowRsvpSidebar = !isTabletOrSmaller && checkHackerPathname();
  const shouldShowRsvpHeader = checkHackerPathname();

  // show if not hidden or is not mobile, and no RSVP sidebar
  const shouldShowSidebar =
    showSidebar && !isTabletOrSmaller && !shouldShowRsvpSidebar;

  const isUnauthorized = isAuthenticated && !hasRsvpAccess(roles, isOrganizer);

  const shouldShowJudgingBanner =
    judging.bannerVisible &&
    (roles.includes(JWTRole.HACKER) || isOrganizer) &&
    location.pathname !== RouteName.HACKER_JUDGING_INFO;
  const judgingBannerHeight = shouldShowJudgingBanner
    ? JUDGING_BANNER_HEIGHT
    : 0;

  return isUnauthorized ? (
    <UnauthorizedError />
  ) : (
    <AppShell>
      {shouldShowJudgingBanner && <JudgingBanner />}
      <Container className="app" $bannerHeight={judgingBannerHeight}>
        <Toast />

        {shouldShowRsvpHeader ? (
          <HackerRsvpHeader currentRoute={shouldShowRsvpHeader} />
        ) : null}
        {shouldShowRsvpSidebar && (
          <HackerRsvpSidebar currentRoute={shouldShowRsvpSidebar} />
        )}
        {shouldShowSidebar && <Sidebar topOffset={judgingBannerHeight} />}
        <MainContent
          $bannerHeight={judgingBannerHeight}
          showNavSidebar={shouldShowSidebar}
          showRsvpSidebar={shouldShowRsvpSidebar}
          showRsvpHeader={shouldShowRsvpHeader}
          sidebarWidth={isTabletOrSmaller ? 0 : sidebarWidth}
          isTabletOrSmaller={isTabletOrSmaller}
        >
          <ScrollToTopWrapper>
            <AppRoutes />
          </ScrollToTopWrapper>
        </MainContent>
      </Container>
    </AppShell>
  );
};

const DashboardAuthProvider = IS_LOCAL_API ? LocalAuthProvider : AuthProvider;

const AuthenticatedApp: React.FC = () => (
  <PDFWorker workerUrl="https://unpkg.com/pdfjs-dist@2.4.456/build/pdf.worker.min.js">
    <QueryClientProvider client={queryClient}>
      <QueryParamProvider ReactRouterRoute={RouteAdapter}>
        <ErrorBoundary FallbackComponent={AwSnap}>
          <DashboardAuthProvider production={IS_PRODUCTION}>
            <UserContextProvider>
              <EventContextProvider>
                <DataProvider>
                  <PermissionsContextProvider>
                    <EnvIndicator />
                    <SidebarContextProvider>
                      <GlobalStyles />
                      <AppContent />
                    </SidebarContextProvider>
                    {!IS_PRODUCTION ? <QA /> : null}
                  </PermissionsContextProvider>
                </DataProvider>
              </EventContextProvider>
            </UserContextProvider>
          </DashboardAuthProvider>
        </ErrorBoundary>
      </QueryParamProvider>
    </QueryClientProvider>
  </PDFWorker>
);

const App: React.FC = () => (
  <North themeDefinition={themeDef}>
    <ApolloProvider client={client}>
      <BrowserRouter>
        <Routes>
          <RouterRoute
            path={BaseRoute.REGISTER}
            element={<Navigate to={BaseRoute.TICKET} replace />}
          />
          <RouterRoute
            path={BaseRoute.TICKET}
            element={
              <ErrorBoundary FallbackComponent={AwSnap}>
                <GlobalStyles />
                <Register />
              </ErrorBoundary>
            }
          />
          <RouterRoute path="*" element={<AuthenticatedApp />} />
        </Routes>
      </BrowserRouter>
    </ApolloProvider>
  </North>
);

export default App;
